import type { PostHog } from 'posthog-js'
import type { RouteLocation } from 'vue-router'
import { useStorage } from '@vueuse/core'
import {
  getAuthStatus,
  requestLogin,
  requestLogout,
  requestRegister,
  requestSetNewPassword,
  requestStartPasswordReset,
  setApiRootUrl,
} from '../service'

const LOGIN_PATH = '/sign-in'
const DEFAULT_PATH = '/'

// Paths served by Django (not Nuxt). After login, a `next=` pointing here
// must use full-page navigation so the browser attaches the fresh session
// cookie; Nuxt's internal router has no route for these and would render
// its catch-all 404. Extend if we add more Django-backed views that redirect
// unauthenticated users through `/sign-in`.
// Deliberately no trailing slash on ``/graphql`` — Django registers the route
// at exactly that path (``path("graphql", …)`` in ``sleuth/urls.py``), with
// ``/graphql/batch`` as a separate route. Explicit both-forms check in
// ``isBackendPath`` avoids accidentally matching a hypothetical
// ``/graphql-admin``.
const BACKEND_PATH_PREFIXES = ['/api/', '/_api/', '/admin/', '/static/']

function isBackendPath(path: string): boolean {
  if (BACKEND_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) return true
  // Exact match on ``/graphql``, and anything under ``/graphql/`` (e.g.
  // ``/graphql/batch``), without matching ``/graphql-admin``.
  if (path === '/graphql' || path.startsWith('/graphql/')) return true
  return false
}

function getRedirectPathFromQuery(route: RouteLocation) {
  return route.query.next ? decodeURIComponent(route.query.next.toString()) : null
}

// Paths that must NEVER be the target of a post-auth redirect. ``?next=``
// values are attacker-controllable (they come from query string) so a
// malicious link like ``/sign-in?next=/api/oauth/authorize/?client_id=evil``
// would land a freshly-logged-in user on the OAuth consent screen for an
// attacker-registered application. Block the consent surface (and the
// per-relay authorize) at the post-auth gate even though the URLs are
// legitimately reachable when the user navigates to them directly.
const DISALLOWED_POST_AUTH_REDIRECT_PREFIXES = ['/api/oauth/authorize', '/relay/']

// Same-origin check applied to every ``?next=`` consumer (the login flow, the
// register/SSO watcher, AND the router middleware). Fails closed on anything
// that isn't a path starting with a single ``/`` — blocks protocol-relative
// ``//evil`` and absolute ``https://evil`` targets from riding a completed
// login into an open redirect — and additionally blocks paths that hand the
// authenticated session to a third-party app via the OAuth consent screen.
function sanitizePostAuthRedirect(redirectPath: string): string | null {
  if (!redirectPath.startsWith('/') || redirectPath.startsWith('//')) return null
  if (DISALLOWED_POST_AUTH_REDIRECT_PREFIXES.some((prefix) => redirectPath.startsWith(prefix))) {
    return null
  }
  return redirectPath
}

// Shared post-auth redirect. Nuxt's router can't navigate to Django-backed
// URLs (e.g. /api/oauth/authorize/) — an internal navigateTo would render the
// Nuxt 404 page with the backend URL stuck in the address bar. For backend
// paths we force a full-page navigation so the browser attaches the fresh
// session cookie to the next request. All three post-auth entry points
// (`login`, the watcher, SSO/register completion) must go through this.
async function redirectAfterAuth(redirectPath: string) {
  const safe = sanitizePostAuthRedirect(redirectPath)
  if (safe === null) {
    await navigateTo(DEFAULT_PATH)
    return
  }
  if (isBackendPath(safe)) {
    window.location.href = safe
    return
  }
  await navigateTo(safe)
}

async function startPasswordReset(email: string) {
  const [success, errors] = await requestStartPasswordReset(email)

  if (success) {
    await navigateTo({ name: 'reset-password-request-success' })
    return null
  } else {
    return errors as Record<string, string[]>
  }
}

async function setNewPassword(uid: string, password: string, passwordRepeated: string) {
  const [success, errors] = await requestSetNewPassword(uid, password, passwordRepeated)

  if (success) {
    await navigateTo({ name: 'reset-password-reset-success' })
    return null
  } else {
    return errors as Record<string, string[]>
  }
}

export default defineNuxtPlugin(async (nuxtApp) => {
  // Skip plugin when rendering error page
  if (nuxtApp.payload.error) return {}

  setApiRootUrl('/_api')

  const isAuthenticated = useStorage('isAuthenticated', false)
  const currentRoute = useRoute()

  function setIsAuthenticated(newStatus: boolean) {
    isAuthenticated.value = newStatus
  }

  async function login(username: string, password: string) {
    const [success, error] = await requestLogin(username, password)

    if (success) {
      setIsAuthenticated(true)
      const redirectPath = getRedirectPathFromQuery(currentRoute)
      if (redirectPath) {
        await redirectAfterAuth(redirectPath)
        return null
      }
      await navigateTo({ path: DEFAULT_PATH, query: currentRoute.query })
    }

    return error ? String(error) : null
  }

  async function logoutAndClearLocalStorage() {
    localStorage.removeItem('last-workspace')
    const [success, error] = await requestLogout()

    if (success) {
      setIsAuthenticated(false)
      // Drop the analytics device id so the next user on this browser isn't
      // attributed to the previous user's person. The plugin-injected nuxtApp
      // isn't typed with other plugins' provides, hence the cast.
      const posthog = nuxtApp.$posthog as (() => PostHog | null) | undefined
      posthog?.()?.reset()
    }

    return error ? String(error) : null
  }

  async function register(
    organizationName: string,
    email: string,
    password: string,
    invitationCode?: string,
    betaAccessCode?: string,
  ) {
    const [success, errors] = await requestRegister(organizationName, email, password, invitationCode, betaAccessCode)

    if (success) {
      setIsAuthenticated(true)

      // New org creation (no invitation) → onboarding wizard
      // Invitation signup → homepage
      await navigateTo(invitationCode ? '/' : '/onboarding-wizard')
      return null
    } else {
      return errors as Record<string, string[]>
    }
  }

  const [currentAuthStatus] = await getAuthStatus()
  setIsAuthenticated(currentAuthStatus)

  addRouteMiddleware(
    'auth',
    (to) => {
      if (to.meta.auth !== false && !isAuthenticated.value) {
        if (to.meta.anonymousAllowed) return

        const query = to.path === DEFAULT_PATH ? {} : { next: encodeURIComponent(to.fullPath) }
        return { path: LOGIN_PATH, query }
      } else if (to.path === LOGIN_PATH && isAuthenticated.value) {
        return { path: DEFAULT_PATH, query: to.query }
      } else if (isAuthenticated.value) {
        const redirectPath = getRedirectPathFromQuery(to)
        if (redirectPath) {
          const safe = sanitizePostAuthRedirect(redirectPath)
          if (safe === null) return
          // Skip internal redirect for Django-backed paths — the login()
          // function handles those via window.location.href so the browser
          // sends the session cookie to nginx → Django. Returning the path
          // here would try to route it through Nuxt and 404.
          if (isBackendPath(safe)) return
          return safe
        }
      }
    },
    { global: true },
  )

  watch(isAuthenticated, async (newStatus) => {
    if (newStatus === false) {
      const cachePlugin = useGqlCache()
      cachePlugin.clearCache()

      if (currentRoute.meta.auth !== false) {
        const query = { next: encodeURIComponent(currentRoute.fullPath) }
        await navigateTo({ path: LOGIN_PATH, query })
      }
    } else {
      const redirectPath = getRedirectPathFromQuery(currentRoute)
      if (currentRoute.path === LOGIN_PATH && redirectPath) await redirectAfterAuth(redirectPath)
    }
  })

  return {
    provide: {
      auth: { login, logoutAndClearLocalStorage, register, startPasswordReset, setNewPassword, isAuthenticated },
    },
  }
})
